import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { workspaceAPI } from '../services/api';
import type { Workspace, CreateWorkspaceData } from '../types';

interface WorkspaceContextType {
  workspaces: Workspace[];
  loading: boolean;
  error: string | null;
  fetchWorkspaces: () => Promise<void>;
  createWorkspace: (data: CreateWorkspaceData) => Promise<Workspace>;
  updateWorkspace: (id: string, data: Partial<Workspace>) => Promise<Workspace>;
  deleteWorkspace: (id: string) => Promise<void>;
  selectWorkspace: (workspace: Workspace) => void;
  selectedWorkspace: Workspace | null;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(() => {
    // Restore selected workspace from localStorage on initial load
    const saved = localStorage.getItem('selectedWorkspace');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await workspaceAPI.getAll();
      setWorkspaces(response.workspaces);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch workspaces');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createWorkspace = async (data: CreateWorkspaceData): Promise<Workspace> => {
    try {
      setLoading(true);
      setError(null);
      const response = await workspaceAPI.create(data);
      setWorkspaces((prev) => [...prev, response.workspace]);
      return response.workspace;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateWorkspace = async (
    id: string,
    data: Partial<Workspace>
  ): Promise<Workspace> => {
    try {
      setLoading(true);
      setError(null);
      const response = await workspaceAPI.update(id, data);
      setWorkspaces((prev) =>
        prev.map((ws) => (ws._id === id ? response.workspace : ws))
      );
      if (selectedWorkspace?._id === id) {
        setSelectedWorkspace(response.workspace);
      }
      return response.workspace;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkspace = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await workspaceAPI.delete(id);
      setWorkspaces((prev) => prev.filter((ws) => ws._id !== id));
      if (selectedWorkspace?._id === id) {
        setSelectedWorkspace(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectWorkspace = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    localStorage.setItem('selectedWorkspace', JSON.stringify(workspace));
  };

  const value = {
    workspaces,
    loading,
    error,
    fetchWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    selectWorkspace,
    selectedWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
